import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import {
  Wallet,
  ArrowUpCircle,
  ArrowDownCircle,
  Loader2,
  History,
  RefreshCw,
  IndianRupee,
  Clock,
  CheckCircle,
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

export default function WalletPage() {
  const { api } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [walletSummary, setWalletSummary] = useState(null);
  const [ledger, setLedger] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [summaryRes, ledgerRes] = await Promise.all([
        api.get('/wallet/summary'),
        api.get('/wallet/ledger?limit=60'),
      ]);
      setWalletSummary(summaryRes.data);
      setLedger(ledgerRes.data.entries || []);
    } catch (error) {
      console.error('Failed to fetch wallet data:', error);
      toast.error('Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatCurrency = (amount, digits = 2) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: digits, maximumFractionDigits: digits }).format(amount || 0);

  const formatDate = (d) => (!d ? '-' : new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }));

  const getLedgerTypeConfig = (type) => {
    const configs = {
      credit: { icon: ArrowDownCircle, color: 'text-green-600', label: 'Credit' },
      debit: { icon: ArrowUpCircle, color: 'text-red-600', label: 'Debit' },
      payout_hold: { icon: Clock, color: 'text-yellow-600', label: 'Payout Hold' },
      payout_release: { icon: CheckCircle, color: 'text-green-600', label: 'Released' },
      admin_adjustment: { icon: IndianRupee, color: 'text-blue-600', label: 'Adjustment' },
      funding_credit: { icon: ArrowDownCircle, color: 'text-green-600', label: 'USDT Funding' },
      fee: { icon: ArrowUpCircle, color: 'text-orange-600', label: 'Fee' },
      reversal: { icon: RefreshCw, color: 'text-purple-600', label: 'Reversal' },
      refund: { icon: ArrowDownCircle, color: 'text-green-600', label: 'Refund' },
    };
    return configs[type] || configs.credit;
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const bal = walletSummary?.balance || {};
  // Build activity trend (chronological) from ledger balance_after
  const trend = [...ledger].reverse().slice(-30).map((e) => ({
    date: new Date(e.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }),
    balance: Math.round(e.balance_after || 0),
  }));

  return (
    <div className="space-y-6" data-testid="wallet-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold">Wallet</h1>
          <p className="text-muted-foreground text-sm">Your balance, funding and payout activity</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => document.getElementById('wallet-ledger')?.scrollIntoView({ behavior: 'smooth' })} data-testid="view-ledger-btn">
            <History className="mr-2 h-4 w-4" />View Ledger
          </Button>
          <Button onClick={() => navigate('/dashboard/wallet/recharge')} data-testid="recharge-wallet-btn">
            <ArrowUpCircle className="mr-2 h-4 w-4" />Recharge Wallet
          </Button>
        </div>
      </div>

      {/* Balance hero + activity chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-1 overflow-hidden border-0 bg-gradient-to-br from-primary to-blue-700 text-white shadow-lg" data-testid="wallet-hero">
          <CardContent className="p-6 relative">
            <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10" />
            <div className="absolute -right-2 top-16 w-24 h-24 rounded-full bg-white/5" />
            <div className="relative">
              <div className="flex items-center gap-2 text-white/80 text-sm"><Wallet className="h-4 w-4" />Available Balance</div>
              <p className="text-4xl font-bold mt-2 font-mono tabular-nums" data-testid="wallet-available">{formatCurrency(bal.available, 0)}</p>
              <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
                <div><p className="text-white/70">Pending</p><p className="font-semibold">{formatCurrency(bal.pending, 0)}</p></div>
                <div><p className="text-white/70">Total Funded</p><p className="font-semibold">{formatCurrency(bal.total_credited, 0)}</p></div>
                <div><p className="text-white/70">Total Debited</p><p className="font-semibold">{formatCurrency(bal.total_debited, 0)}</p></div>
                <div><p className="text-white/70">Total Fees</p><p className="font-semibold">{formatCurrency(bal.total_fees, 0)}</p></div>
              </div>
              <Button className="mt-6 w-full bg-white text-primary hover:bg-white/90" onClick={() => navigate('/dashboard/wallet/recharge')} data-testid="hero-recharge-btn">
                <ArrowUpCircle className="mr-2 h-4 w-4" />Recharge Wallet
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2" data-testid="wallet-activity-chart">
          <CardHeader><CardTitle className="text-lg font-heading">Wallet Activity</CardTitle><CardDescription>Balance movement over recent transactions</CardDescription></CardHeader>
          <CardContent>
            <div className="h-[240px]">
              {trend.length === 0 ? (
                <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No activity yet</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%" minHeight={0}>
                  <AreaChart data={trend} margin={{ left: 4, right: 8, top: 8 }}>
                    <defs><linearGradient id="wbal" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#2563EB" stopOpacity={0.35} /><stop offset="95%" stopColor="#2563EB" stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                    <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} />
                    <Tooltip contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} formatter={(v) => formatCurrency(v, 0)} />
                    <Area type="monotone" dataKey="balance" name="Balance" stroke="#2563EB" fill="url(#wbal)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Wallet Ledger */}
      <Card id="wallet-ledger" data-testid="wallet-ledger">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2"><History className="w-5 h-5" />Wallet Ledger</span>
            <Button variant="ghost" size="sm" onClick={fetchData} data-testid="ledger-refresh-btn"><RefreshCw className="h-4 w-4" /></Button>
          </CardTitle>
          <CardDescription>Every credit and debit with running balance</CardDescription>
        </CardHeader>
        <CardContent>
          {ledger.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground"><History className="w-12 h-12 mx-auto opacity-50 mb-4" /><p>No transactions yet</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[620px]">
                <thead>
                  <tr className="border-b text-muted-foreground text-left">
                    <th className="py-2 px-3">Date</th>
                    <th className="py-2 px-3">Reference</th>
                    <th className="py-2 px-3">Type</th>
                    <th className="py-2 px-3 text-right">Credit</th>
                    <th className="py-2 px-3 text-right">Debit</th>
                    <th className="py-2 px-3 text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.map((e) => {
                    const cfg = getLedgerTypeConfig(e.entry_type);
                    const isCredit = (e.amount || 0) >= 0;
                    return (
                      <tr key={e.id} className="border-b last:border-0 hover:bg-muted/40" data-testid={`ledger-row-${e.id}`}>
                        <td className="py-2.5 px-3 whitespace-nowrap text-xs text-muted-foreground">{formatDate(e.created_at)}</td>
                        <td className="py-2.5 px-3 font-mono text-xs">{e.reference_id || '—'}</td>
                        <td className="py-2.5 px-3"><span className={`inline-flex items-center gap-1 ${cfg.color}`}><cfg.icon className="w-3.5 h-3.5" />{cfg.label}</span></td>
                        <td className="py-2.5 px-3 text-right font-medium text-green-600">{isCredit ? formatCurrency(Math.abs(e.amount)) : '—'}</td>
                        <td className="py-2.5 px-3 text-right font-medium text-red-600">{!isCredit ? formatCurrency(Math.abs(e.amount)) : '—'}</td>
                        <td className="py-2.5 px-3 text-right font-mono">{formatCurrency(e.balance_after)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
